import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import invariant from 'tiny-invariant'
import {
  getToolToFollow,
  LATEST_SCAN_EMBED,
  resolveBranch,
  useCachedRepositoryBranch,
  useGetKnownRepositoryBranches,
} from '../../components/menus/branch-selector.hooks'
import { useGetToolsForRepository } from '../../components/menus/tool-selector.hooks'
import {
  fetchRepositoryBranchesByName,
  useGetRepository,
  useGetRepositoryBranches,
  useGetScans,
} from '../../utils/api-client/api-hooks'
import { ToolSchema, type Tool } from '../../utils/api-client/api-schemas.ts'
import { isAiScannerTool } from '../../utils/detector-utils.ts'
import { patchSearchParams } from './scans-list.tsx'

export const useRepositoryDetailsPageParams = () => {
  const { repositoryId } = useParams()
  invariant(repositoryId, 'Missing required parameter: repositoryId')
  return useMemo(() => ({ repositoryId }), [repositoryId])
}

export function useSelectedTool() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTool = ToolSchema.safeParse(searchParams.get('tool')).data ?? null

  const toggleSelectedTool = useCallback(
    (tool: Tool | null) => {
      setSearchParams(
        prev => {
          if (tool) {
            prev.set('tool', tool)
          } else {
            prev.delete('tool')
          }
          return prev
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return useMemo(() => ({ selectedTool, toggleSelectedTool }), [selectedTool, toggleSelectedTool])
}

/**
 * Switches the tool the reader picked, keeping the branch when the new tool has scanned it.
 *
 * The branch is checked before the URL changes, so the tool and branch in the URL always agree: a
 * `?branch=` the selected tool never scanned is otherwise read as a branch link to follow, which
 * would switch the tool straight back (see `getToolToFollow`). When the new tool has not scanned the
 * branch, or the check fails, the branch is dropped and the first-load rule picks one.
 */
export function useSwitchTool() {
  const queryClient = useQueryClient()
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: repository } = useGetRepository({ repositoryId })
  const branchesHref = repository?._links.branches?.href
  const [, setSearchParams] = useSearchParams()
  const { urlBranch } = useSelectedBranch()

  return useCallback(
    async (tool: Tool) => {
      const branches =
        urlBranch && branchesHref
          ? await fetchRepositoryBranchesByName(queryClient, { branchesHref, tool, names: [urlBranch] }).catch(
              (error: unknown) => {
                // Keeping a branch that could not be checked would follow it back to another tool, so
                // the switch goes ahead without it; the warning keeps the dropped branch explicable.
                console.warn(`Could not check whether ${tool} scanned branch '${urlBranch}'; dropping it`, error)
                return undefined
              }
            )
          : undefined
      const keepsBranch = !!urlBranch && !!branches?._embedded.items.some(branch => branch.name === urlBranch)
      setSearchParams(prev => patchSearchParams(prev, { tool, branch: keepsBranch ? urlBranch : null }), {
        replace: true,
      })
    },
    [queryClient, branchesHref, urlBranch, setSearchParams]
  )
}

export function useSelectedBranch() {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlBranch = searchParams.get('branch') ?? undefined

  /** For the page's own corrections to the URL, which should not reset where the reader is. */
  const replaceUrlBranch = useCallback(
    (branch: string | undefined) => {
      setSearchParams(prev => patchSearchParams(prev, { branch: branch ?? null }), { replace: true })
    },
    [setSearchParams]
  )

  /** For the reader picking a branch: the scans list's page belongs to the previous branch, so it goes. */
  const selectBranch = useCallback(
    (branch: string) => {
      setSearchParams(prev => patchSearchParams(prev, { branch, 'page-number': null }), { replace: true })
    },
    [setSearchParams]
  )

  return useMemo(() => ({ urlBranch, replaceUrlBranch, selectBranch }), [urlBranch, replaceUrlBranch, selectBranch])
}

export type ResolvedBranch = {
  /** The branch to filter the tool's scans by; `undefined` to leave them unfiltered. */
  branch?: string
  /** True while it is not yet known which branch applies, so callers can hold their queries. */
  isLoading: boolean
  /** Whether the selected tool has scanned any branch, which is when the selector is offered. */
  hasBranches: boolean
  isError: boolean
  /** The tool with the most recent scan on the URL's branch, when the selected tool never scanned it. */
  toolToFollow?: Tool
}

/**
 * The branch the Findings and Scans tabs, and the newer-scan banner, are scoped to.
 *
 * Derived during render rather than read back from the URL, so the tabs apply the right branch on
 * the first load and right after a tool switch, without first rendering the wrong one while the URL
 * catches up (see `resolveBranch` for the rule). `useSelectBranchOnLoad` then writes it to the URL.
 */
export function useResolvedBranch(): ResolvedBranch {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { selectedTool } = useSelectedTool()
  const { urlBranch } = useSelectedBranch()
  const { data: repository, isLoading: isRepositoryLoading } = useGetRepository({ repositoryId })
  const { data: availableTools, isLoading: isToolsLoading } = useGetToolsForRepository({ repositoryId })
  const tool = selectedTool ?? undefined
  // Followed rather than built, per the hypermedia ADR. A platform without the endpoint omits the
  // link, and the tabs then fall back to every branch.
  const branchesHref = repository?._links.branches?.href
  const hasBranchesLink = !!branchesHref

  const cachedUrlBranch = useCachedRepositoryBranch({ branchesHref, tool, name: urlBranch })
  const {
    knownNames,
    knownBranches,
    isError: isKnownError,
    isRecentlyVisitedLoading,
  } = useGetKnownRepositoryBranches({ repositoryId, tool, urlBranch })
  const latest = useGetRepositoryBranches({
    branchesHref,
    tool,
    page: { number: 0, size: 1 },
    enabled: !!selectedTool,
  })
  const isKnownLoading = !!selectedTool && hasBranchesLink && knownNames.length > 0 && !knownBranches && !isKnownError

  const isUrlBranchUnknown =
    !!urlBranch && !cachedUrlBranch && !!knownBranches && !knownBranches.some(branch => branch.name === urlBranch)
  const urlBranchFromAnyTool = useGetRepositoryBranches({
    branchesHref,
    names: urlBranch ? [urlBranch] : [],
    embed: LATEST_SCAN_EMBED,
    page: { number: 0, size: 1 },
    enabled: !!selectedTool && isUrlBranchUnknown,
  })

  const isError = latest.isError || isKnownError
  const isLoading =
    !!selectedTool &&
    !cachedUrlBranch &&
    !isError &&
    (isRepositoryLoading ||
      isRecentlyVisitedLoading ||
      latest.isLoading ||
      isKnownLoading ||
      (isUrlBranchUnknown && (urlBranchFromAnyTool.isLoading || isToolsLoading)))

  return useMemo(() => {
    if (!selectedTool || isError || (!isRepositoryLoading && !hasBranchesLink)) {
      // Without the branches endpoint (e.g. an older platform), the tabs fall back to every branch.
      return { branch: undefined, isLoading: false, hasBranches: false, isError }
    }
    if (cachedUrlBranch) {
      return { branch: cachedUrlBranch.name, isLoading: false, hasBranches: true, isError: false }
    }
    if (isLoading) {
      return { branch: undefined, isLoading: true, hasBranches: false, isError: false }
    }
    const toolToFollow = getToolToFollow({
      urlBranch,
      selectedTool,
      knownBranches: knownBranches ?? [],
      urlBranchFromAnyTool: urlBranchFromAnyTool.data?._embedded.items[0],
      availableTools,
    })
    if (toolToFollow) {
      return { branch: undefined, isLoading: true, hasBranches: false, isError: false, toolToFollow }
    }
    return {
      branch: resolveBranch({
        urlBranch,
        knownBranches: knownBranches ?? [],
        latestBranch: latest.data?._embedded.items[0],
      }),
      isLoading: false,
      hasBranches: (latest.data?.total ?? 0) > 0,
      isError: false,
    }
  }, [
    selectedTool,
    isError,
    isRepositoryLoading,
    hasBranchesLink,
    availableTools,
    cachedUrlBranch,
    isLoading,
    knownBranches,
    urlBranch,
    urlBranchFromAnyTool.data,
    latest.data,
  ])
}

/**
 * The branch a tool-scoped query filters by, and whether it may run yet. Every consumer holds its
 * queries until the branch resolves, so none fetches every branch first and the right one after.
 */
export function useBranchFilter() {
  const { branch, isLoading } = useResolvedBranch()
  return { branch, enabled: !isLoading }
}

/**
 * The branch the header's selector shows, or `undefined` for no selector.
 *
 * While the branch resolves — right after a tool switch, say — the header keeps showing the last
 * resolved branch instead of falling back to the plain default-branch tag for a moment and then
 * swapping the selector back in. The selector's list shows "Loading branches…" meanwhile, since the
 * new tool's branches are not known yet.
 *
 * `placeholderData` cannot do this: the branch comes from several queries, and across a tool switch
 * their previous data is deliberately not kept, since it would list the other tool's branches. What
 * is kept here is only the name in the trigger, never the list.
 */
export function useShownBranch(): string | undefined {
  const { branch, hasBranches, isLoading } = useResolvedBranch()
  const resolvedBranch = hasBranches ? branch : undefined
  const [lastResolvedBranch, setLastResolvedBranch] = useState<string | undefined>()
  if (!isLoading && resolvedBranch !== lastResolvedBranch) {
    // Adjusting state while rendering, as React documents for deriving it from a changing value.
    setLastResolvedBranch(resolvedBranch)
  }
  return isLoading ? lastResolvedBranch : resolvedBranch
}

/**
 * Keeps `?branch=` in step with the resolved branch, so the URL always names what the page shows, and
 * switches the tool when the URL's branch is one only another tool has scanned.
 */
export function useSelectBranchOnLoad() {
  const { selectedTool, toggleSelectedTool } = useSelectedTool()
  const { urlBranch, replaceUrlBranch } = useSelectedBranch()
  const { branch, isLoading, isError, toolToFollow } = useResolvedBranch()

  useEffect(() => {
    if (toolToFollow) {
      toggleSelectedTool(toolToFollow)
      return
    }
    // Without a tool yet, or with branches unavailable, there is nothing to correct the URL to.
    if (!selectedTool || isLoading || isError) {
      return
    }
    if (branch !== urlBranch) {
      replaceUrlBranch(branch)
    }
  }, [toolToFollow, toggleSelectedTool, selectedTool, isLoading, isError, branch, urlBranch, replaceUrlBranch])
}

export function useSelectToolOnLoad() {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: latestScans, isLoading: isLatestScansLoading } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
  })
  const latestScanTool = latestScans?._embedded?.items?.[0]?.detector

  const { selectedTool, toggleSelectedTool } = useSelectedTool()
  const { data: tools, mappedTools, isLoading: isToolsLoading } = useGetToolsForRepository({ repositoryId })

  const toolToSelect =
    isLatestScansLoading || isToolsLoading ? undefined : pickInitialTool({ latestScanTool, mappedTools, tools })

  useEffect(() => {
    if (toolToSelect && !selectedTool) {
      toggleSelectedTool(toolToSelect)
    }
  }, [toolToSelect, selectedTool, toggleSelectedTool])
}

function pickInitialTool({
  latestScanTool,
  mappedTools,
  tools,
}: {
  latestScanTool: Tool | undefined
  mappedTools: Set<Tool>
  tools: Set<Tool>
}) {
  if (latestScanTool && tools.has(latestScanTool)) {
    return latestScanTool
  }
  // Choosing a built-in scanner for a repository with no tool or scan of its own would hide the
  // "No sources detected" onboarding that asks its owner to map one.
  return [...mappedTools][0] ?? [...tools].find(tool => !isAiScannerTool(tool))
}
